import endpoints from '@app/src/api/endpoints'
import { FetchKey, useFetchCollectionWithPost } from '@app/src/api/fetchHooks'
import { COMPANY_QUESTIONNAIRE_QUESTION_KEYS } from '@app/src/components/CreateNewRequestModal/CompanyQuestionnaire'
import ProviderSelectorWithFilters from '@app/src/components/CreateNewRequestModal/ProviderSelectorWithFilters'
import CreationModalContainer from '@app/src/components/CreationModal/CreationModalContainer'
import CreationModalStep from '@app/src/components/CreationModal/CreationModalStep'
import { useCreationModalProgress } from '@app/src/context/CreationModalProgressContext'
import { ProvidersModalPurpose, useProvidersModal } from '@app/src/context/ProvidersModalContext'
import SelectAdverseScreeningQuestionnairesForScraping from '@app/src/pages/Findings/SelectAdverseScreeningQuestionnairesForScraping'
import SelectTemplateForScraping from '@app/src/pages/ResourceCollection/Collections/Provider/ScheduleScreening/SelectTemplateForScraping'
import { Operators } from '@app/src/pages/ResourceCollection/Filters/useFilters'
import type { Provider } from '@app/src/types/organizations'
import type { QuestionnaireTemplate } from '@app/src/types/resourceExplorer'
import { Box, Dialog, Stack } from '@mui/material'
import React, { useEffect } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useIntl } from 'react-intl'

type TemplateAiScreeningStarterModalProps = {
  open: boolean
  onClose: () => void
  selectedQuestionnaireTemplates?: Pick<QuestionnaireTemplate, 'id' | 'title'>[]
  /** Findings' entry point: lets the user pick from the 4 adverse screening questionnaires instead of a generic template list. */
  isAdverseScreeningQuestionnaires?: boolean
}

type FormData = {
  templates: Array<{ id: number; title: string }>
  responders: Array<{ id: number; name: string; organizationId: number }>
}

const TEMPLATE_AI_SCREENING_QUESTION_KEYS = {
  TEMPLATES: 'templates',
} as const

const WIDTH = 780
// The schedule screening modal continues this flow and always ends with its summary step.
const SUMMARY_STEP_COUNT = 1

const TemplateAiScreeningStarterModal: React.FC<TemplateAiScreeningStarterModalProps> = ({
  open,
  onClose,
  selectedQuestionnaireTemplates,
  isAdverseScreeningQuestionnaires = false,
}) => {
  const { formatMessage } = useIntl()
  const { activeStep, totalSteps, setActiveStep, setTotalSteps } = useCreationModalProgress()
  const { handleOpenConnectDialog } = useProvidersModal()

  const formMethods = useForm<FormData>({
    shouldUnregister: false,
    defaultValues: {
      templates: selectedQuestionnaireTemplates,
      responders: [],
    },
  })

  const { control, handleSubmit, setValue, reset, watch } = formMethods
  const selectedResponders = watch(COMPANY_QUESTIONNAIRE_QUESTION_KEYS.RESPONDERS) ?? []
  const selectedProviderIds = selectedResponders.map(responder => responder.id)

  const { items: selectedProviders = [], isFetching: isLoadingProviders } = useFetchCollectionWithPost<Provider>({
    endpoint: endpoints.providersCollection,
    key: [FetchKey.Provider, 'template-ai-screening-starter', selectedProviderIds.join(',')],
    payload: {
      include: ['enrichments'],
      filter: [
        {
          name: 'id',
          filters: [{ value: selectedProviderIds, operator: Operators.In }],
        },
      ],
      pagination: { pageNumber: 1, itemsPerPage: 5_000 },
    },
    options: {
      enabled: selectedProviderIds.length > 0,
    },
  })

  const QUESTIONS = [
    {
      title: formatMessage({
        id: isAdverseScreeningQuestionnaires
          ? 'findings.aiScreeningModal.selectScreening.title'
          : 'form.scheduleProviderScreening.selectQuestionnaire.title',
      }),
      description: formatMessage({
        id: isAdverseScreeningQuestionnaires
          ? 'findings.aiScreeningModal.selectScreening.description'
          : 'form.createRequest.selectTemplateBody',
      }),
      fieldnames: [TEMPLATE_AI_SCREENING_QUESTION_KEYS.TEMPLATES],
      disableInitialBack: true,
      widthOverride: WIDTH,
      children: isAdverseScreeningQuestionnaires ? (
        <SelectAdverseScreeningQuestionnairesForScraping name={TEMPLATE_AI_SCREENING_QUESTION_KEYS.TEMPLATES} />
      ) : (
        <SelectTemplateForScraping name={TEMPLATE_AI_SCREENING_QUESTION_KEYS.TEMPLATES} />
      ),
    },
    {
      title: formatMessage({ id: 'form.createRequest.selectCompaniesTitle' }),
      description: formatMessage({ id: 'form.createRequest.selectCompaniesBody' }),
      fieldnames: [COMPANY_QUESTIONNAIRE_QUESTION_KEYS.RESPONDERS],
      overrideButtonTextId: 'general.continue',
      widthOverride: WIDTH,
      isLoading: selectedProviderIds.length > 0 && isLoadingProviders,
      children: <ProviderSelectorWithFilters control={control} setValue={setValue} includeOnlyLinked={false} />,
    },
  ]

  const activeQuestion = QUESTIONS[activeStep - 1]

  const startScheduleScreeningFlow = handleSubmit(values => {
    if (!selectedProviders.length || !values.templates.length) {
      return
    }

    handleOpenConnectDialog(
      selectedProviders,
      ProvidersModalPurpose.ScheduleScreening,
      undefined,
      values.templates,
      isAdverseScreeningQuestionnaires,
      QUESTIONS.length,
    )
    onClose()
  })

  useEffect(() => {
    setTotalSteps(QUESTIONS.length)
  }, [setTotalSteps, QUESTIONS.length])

  useEffect(() => {
    if (open) {
      setActiveStep(1)
      reset({
        templates: selectedQuestionnaireTemplates,
        responders: [],
      })
    }
  }, [open, setActiveStep, reset, JSON.stringify(selectedQuestionnaireTemplates)])

  return (
    <Dialog open={open} onClose={onClose} fullScreen>
      <CreationModalContainer
        title={formatMessage({ id: 'general.aiScreening' })}
        onClose={onClose}
        displayedTotalSteps={totalSteps + SUMMARY_STEP_COUNT}
      >
        <FormProvider {...formMethods}>
          <Box component="form" onSubmit={startScheduleScreeningFlow} width="100%" height="100%">
            <Stack flexGrow={1} alignItems="center" height="100%">
              {activeQuestion && <CreationModalStep {...activeQuestion}>{activeQuestion.children}</CreationModalStep>}
            </Stack>
          </Box>
        </FormProvider>
      </CreationModalContainer>
    </Dialog>
  )
}

export default TemplateAiScreeningStarterModal
